import { useContext } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    AppContext,
    SttFacilityList,
    SttFormText,
    SttFormCheck,
    SttFormTextarea,
    SttSelectDeviceStatus,
    SttSelectUnlockerType,
} from '../../../sporttia/all';
import SimulateAccessDialog from './SimulateAccessDialog';
import constants from '../../../config/constants';
import { PERMISSIONS } from '../../../lib/permissions';
import {
    LEGACY_PRIVILEGES,
    canOrHasPrivilege,
} from '../../../lib/permissionOrPrivilege';
import type { Facility } from '../../../types/models/Facility';

type Device = {
    id?: string;
    name?: string;
    type?: string;
    description?: string;
    status?: string;
    signature?: string;
    preLight?: number;
    postLight?: number;
    preCortesy?: number;
    margin?: number;
    soundTime?: number;
    pendingPaymentsMargin?: number;
    antiPassback?: boolean;
    antiPassbackBonos?: boolean;
    checkDirectionOut?: boolean;
    remoteOpening?: boolean;
    facilities?: Facility[];
    trash?: boolean;
};

type Props = {
    device?: Device;
    scId?: number | null;
    setProperty?: (property: {
        name: string | undefined;
        value: unknown;
    }) => void;
    setSimulateAccessOpen?: (open: boolean) => void;
    simulateAccessOpen?: boolean;
    id?: string | undefined;
};

export default function DeviceDetails({
    device,
    scId,
    setProperty,
    setSimulateAccessOpen,
    simulateAccessOpen,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();

    const checkIfUserSC = cxt?.logged && cxt?.user?.role !== 'ADMIN';
    // CU-8697paaj1: privilegio SC "Gestión de dispositivos" (id 29).
    // 869fed7ht: con los roles v8 encendidos, `device.modify`.
    // Sporttia ADMIN siempre puede editar; los SC sólo con el permiso.
    const canEditDevice =
        !checkIfUserSC ||
        canOrHasPrivilege(
            cxt,
            PERMISSIONS.DEVICE.MODIFY,
            LEGACY_PRIVILEGES.MANAGE_DEVICES,
        );
    const isReadOnly = !canEditDevice;

    // Añadir facility a este device y luego añadir localmente el 'facility' a device.facilities
    function addFacility(facility: Facility): void {
        cxt?.api(
            'POST',
            `${constants.boundedContextPrefixes.MANAGER}/devices/${device?.id}/facilities/${facility.id}`,
            {
                success: () => {
                    setProperty?.({
                        name: 'facilities',
                        value: (device?.facilities || []).concat([facility]),
                    });
                },
            },
        );
    }

    // Eliminar facility de este device y luego quitar localmente el 'facility' de device.facilities
    function removeFacility(facility: Facility): void {
        cxt?.api(
            'DELETE',
            `${constants.boundedContextPrefixes.MANAGER}/devices/${device?.id}/facilities/${facility.id}`,
            {
                confirmation: true,
                success: () => {
                    setProperty?.({
                        name: 'facilities',
                        value:
                            device?.facilities?.filter(
                                (f) => f.id !== facility.id,
                            ) || [],
                    });
                },
            },
        );
    }

    // Render
    return (
        <>
            {device && (
                <Box p={2}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, lg: 8 }}>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 12, sm: 8 }}>
                                    <SttFormText
                                        caption={t('Name')}
                                        name="name"
                                        defVal={device.name}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4 }}>
                                    <SttSelectUnlockerType
                                        name="type"
                                        defVal={device.type || ''}
                                        onChange={setProperty!}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <SttFormTextarea
                                        caption={t('Description')}
                                        name="description"
                                        defVal={device.description}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttSelectDeviceStatus
                                        caption={t('Status')}
                                        name="status"
                                        defVal={device.status || ''}
                                        onChange={setProperty!}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <SttFormText
                                        caption={t('Signature')}
                                        name="signature"
                                        defVal={device.signature}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <SttFormText
                                        caption={t('PreLight')}
                                        name="preLight"
                                        defVal={device.preLight?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <SttFormText
                                        caption={t('PostLight')}
                                        name="postLight"
                                        defVal={device.postLight?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <SttFormText
                                        caption={t('PreCortesy')}
                                        name="preCortesy"
                                        defVal={device.preCortesy?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <SttFormText
                                        caption={t('Margin')}
                                        name="margin"
                                        defVal={device.margin?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <SttFormText
                                        caption={t('SirenTime')}
                                        name="soundTime"
                                        defVal={device.soundTime?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                    <SttFormText
                                        caption={t('PendingPaymentsMargin')}
                                        name="soundTime"
                                        defVal={device.pendingPaymentsMargin?.toString()}
                                        onChange={setProperty}
                                        disabled={isReadOnly}
                                    />
                                </Grid>

                                <Grid size={{ xs: 12, sm: 12, md: 12 }}>
                                    {/* --- Anti Passback --- */}
                                    <Box>
                                        <SttFormCheck
                                            caption={t('AntiPassback')}
                                            name="antiPassback"
                                            checked={device.antiPassback}
                                            onChange={setProperty}
                                            disabled={isReadOnly}
                                        />
                                    </Box>

                                    {/* --- Anti Passback en bonos de consumo --- */}
                                    <Box>
                                        <SttFormCheck
                                            caption={t('AntiPassbackBonos')}
                                            name="antiPassbackBonos"
                                            checked={device.antiPassbackBonos}
                                            onChange={setProperty}
                                            disabled={
                                                isReadOnly ||
                                                !device.antiPassback
                                            }
                                        />
                                    </Box>

                                    {/* --- Checking direction out --- */}
                                    <Box>
                                        <SttFormCheck
                                            caption={t(
                                                'CheckingDirectionOutDesc',
                                            )}
                                            name="checkDirectionOut"
                                            checked={device.checkDirectionOut}
                                            onChange={setProperty}
                                            disabled={isReadOnly}
                                        />
                                    </Box>

                                    {/* --- Remote opening --- */}
                                    <Box>
                                        <SttFormCheck
                                            caption={t('RemoteOpeningDesc')}
                                            name="remoteOpening"
                                            checked={device.remoteOpening}
                                            onChange={setProperty}
                                            disabled={isReadOnly}
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid size={{ xs: 12, lg: 4 }}>
                            {device.id && !device.trash && (
                                <SttFacilityList
                                    permission={PERMISSIONS.DEVICE.MODIFY}
                                    facilities={
                                        (device.facilities as any) || []
                                    }
                                    onAdd={(facility: any) =>
                                        addFacility(facility)
                                    }
                                    onDelete={(facility: any) =>
                                        removeFacility(facility)
                                    }
                                    scId={scId}
                                    disabled={isReadOnly}
                                />
                            )}
                        </Grid>
                    </Grid>
                </Box>
            )}

            {/* --- Dilalog for simulating --- */}
            <SimulateAccessDialog
                open={simulateAccessOpen || false}
                device={device}
                onClose={() => setSimulateAccessOpen?.(false)}
            />
        </>
    );
}
